import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '@truecare/api-client';
import type { HomecareMeResponse, RecipientResponse } from '@truecare/schemas';
import { Alert, Button, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';
import { RecipientForm } from './RecipientForm';

/**
 * Homecare-client portal building blocks (the `/me` branch when the user has
 * HOMECARE_CLIENT_USER persona). Composed into tabs by <HomecareWorkspace> in
 * routes/me.tsx:
 *   - <HomecareAccountSummary> — payment mode/status + preferences (Profile tab)
 *   - <RecipientsSection> — the people care is booked for (Recipients tab)
 * The SUSPENDED/TERMINATED banners live in the PersonaWorkspace banner slot.
 */
export function HomecareAccountSummary({ data }: { data: HomecareMeResponse }) {
  const paymentReady =
    data.paymentMode === 'INVOICE' ||
    (data.hasStripeCustomer && data.hasDefaultPaymentMethod);

  return (
    <Card padding="lg" accent="left" tone="gold">
      <CardHeader
        title="Payment & preferences"
        description="How you're billed, plus your account preferences."
      />
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
        <ProfileField label="Payment method" value={data.paymentMode} />
        <ProfileField label="Language" value={data.languagePreference} />
        <ProfileField label="Status" value={data.status} />
        <ProfileField
          label="Payment setup"
          value={
            paymentReady
              ? 'Ready'
              : data.hasStripeCustomer
                ? 'Add a payment method'
                : 'Pending — TrueCare™ will reach out'
          }
        />
      </dl>
      {!paymentReady && data.paymentMode === 'STRIPE' && (
        <Alert tone="info" title="Payment setup needed before your first booking" className="mt-6">
          Add a payment method on the <strong>Billing</strong> tab. Visit charges
          are applied automatically to your default card.
        </Alert>
      )}
    </Card>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-body text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </dt>
      <dd className="mt-0.5 font-body text-sm text-ink">{value}</dd>
    </div>
  );
}

export function RecipientsSection({
  recipients,
  editable,
  removable,
}: {
  recipients: RecipientResponse[];
  /** True when the account is ACTIVE — gates Add + Edit (backend blocks those
   *  writes on SUSPENDED). */
  editable: boolean;
  /** Audit #19 — true when the account is NOT TERMINATED. Archiving a recipient
   *  is allowed on SUSPENDED accounts (backend archiveRecipient only blocks
   *  TERMINATED), so Remove must not be hidden behind `editable`. */
  removable: boolean;
}) {
  const [addingNew, setAddingNew] = useState(false);

  return (
    <Card padding="lg">
      <CardHeader
        title="My recipients"
        description={
          recipients.length === 0
            ? 'Add the people you\'d like care for. You can add more than one — yourself, family members, or anyone whose care you\'re managing.'
            : `${String(recipients.length)} ${recipients.length === 1 ? 'recipient' : 'recipients'} under this account.`
        }
      />

      {recipients.length === 0 && !addingNew && (
        <div className="mt-2 rounded-md border border-dashed border-border bg-paper-warm p-6 text-center">
          <p className="font-body text-sm text-ink-muted">
            You haven't added anyone yet.
          </p>
          {editable && (
            <Button onClick={() => setAddingNew(true)} className="mt-4">
              Add your first recipient
            </Button>
          )}
        </div>
      )}

      {recipients.length > 0 && (
        <ul className="flex flex-col gap-3">
          {recipients.map((r) => (
            <RecipientRow key={r.id} recipient={r} editable={editable} removable={removable} />
          ))}
        </ul>
      )}

      {!addingNew && recipients.length > 0 && editable && (
        <div className="mt-6">
          <Button variant="secondary" onClick={() => setAddingNew(true)}>
            + Add another recipient
          </Button>
        </div>
      )}

      {addingNew && (
        <div className="mt-6 rounded-md border border-border bg-paper-warm p-6">
          <h3 className="font-heading text-xl text-forest mb-4">
            Add a recipient
          </h3>
          <RecipientForm
            mode="create"
            onSuccess={() => setAddingNew(false)}
            onCancel={() => setAddingNew(false)}
          />
        </div>
      )}
    </Card>
  );
}

function RecipientRow({
  recipient,
  editable,
  removable,
}: {
  recipient: RecipientResponse;
  editable: boolean;
  removable: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const archiveMutation = useMutation({
    mutationFn: () =>
      unwrap(
        api.DELETE('/api/v1/me/homecare/recipients/{id}', {
          params: { path: { id: recipient.id } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['homecare', 'me'] });
    },
  });

  const address = [
    recipient.serviceAddressLine1,
    recipient.serviceAddressLine2,
    recipient.serviceAddressCity,
    recipient.serviceAddressProvince,
    recipient.serviceAddressPostalCode,
  ]
    .filter((p): p is string => Boolean(p))
    .join(', ');

  return (
    <li className="rounded-md border border-border bg-paper-warm p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className="whitespace-nowrap font-heading text-lg text-forest">
              {recipient.name}
            </span>
            <span className="font-body text-xs text-ink-muted">
              {formatRelationship(recipient.relationshipToAccountHolder)}
            </span>
          </div>
          <p className="mt-1 font-body text-sm text-ink-muted">{address}</p>
          {!recipient.geocoded && (
            <p className="mt-1 font-body text-xs text-warning">
              Address pending verification — care booking will be available
              once we've confirmed this location.
            </p>
          )}
        </div>
        {!editing && (editable || removable) && (
          <div className="flex gap-2">
            {/* Edit requires ACTIVE (backend blocks patch on SUSPENDED). */}
            {editable && (
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {/* Audit #19 — Remove is allowed on SUSPENDED (only TERMINATED blocks
                archive), so it's gated on `removable`, not `editable`. */}
            {removable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmArchive(true)}
                disabled={archiveMutation.isPending}
              >
                Remove
              </Button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="mt-4 border-t border-border pt-4">
          <RecipientForm
            mode="edit"
            recipientId={recipient.id}
            initial={{
              name: recipient.name,
              dateOfBirth: recipient.dateOfBirth,
              relationshipToAccountHolder: recipient.relationshipToAccountHolder,
              serviceAddressLine1: recipient.serviceAddressLine1,
              serviceAddressLine2: recipient.serviceAddressLine2,
              serviceAddressCity: recipient.serviceAddressCity,
              serviceAddressProvince: recipient.serviceAddressProvince,
              serviceAddressPostalCode: recipient.serviceAddressPostalCode,
              timezone: recipient.timezone,
            }}
            onSuccess={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        </div>
      )}

      {confirmArchive && (
        <div className="mt-4 border-t border-border pt-4">
          <p className="font-body text-sm text-ink">
            Remove <strong>{recipient.name}</strong> from your account?
            Past bookings (when available) will keep their record. You
            can't undo this from the portal — contact TrueCare™ if you
            need to add them back.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              variant="danger"
              size="sm"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? 'Removing…' : 'Yes, remove'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmArchive(false)}
              disabled={archiveMutation.isPending}
            >
              Cancel
            </Button>
          </div>
          {archiveMutation.isError && (
            <p className="mt-2 font-body text-xs text-danger">
              Couldn't remove — please try again.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function formatRelationship(rel: string): string {
  const map: Record<string, string> = {
    SELF: 'myself',
    SPOUSE: 'spouse',
    ADULT_CHILD: 'parent',
    PARENT: 'child',
    SIBLING: 'sibling',
    POA: 'power of attorney',
    GUARDIAN: 'legal guardian',
    OTHER: 'other',
  };
  return map[rel] ?? rel.toLowerCase();
}
